// src/components/ui/AppDrawer.tsx
import { useEffect } from 'react';
import { Drawer } from 'vaul';
import type { ReactNode } from 'react';
import { useBackHandlerStore } from '@/lib/backHandlers';

interface AppDrawerProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  description?: string;
  children: ReactNode;
  footer?: ReactNode;
  dismissible?: boolean;
}

export function AppDrawer({
  open,
  onOpenChange,
  title,
  description,
  children,
  footer,
  dismissible = true,
}: AppDrawerProps) {
  useEffect(() => {
    if (!open) return;
    const unsub = useBackHandlerStore.getState().push(() => onOpenChange(false));
    return unsub;
  }, [open, onOpenChange]);

  return (
    <Drawer.Root open={open} onOpenChange={onOpenChange} dismissible={dismissible}>
      <Drawer.Portal>
        <Drawer.Overlay className="fixed inset-0 z-40 bg-black/40" />
        <Drawer.Content
          className="fixed bottom-0 left-0 right-0 z-50 mt-24 flex max-h-[85vh] flex-col rounded-t-2xl bg-card outline-none"
          style={{ paddingBottom: 'env(safe-area-inset-bottom)' }}
        >
          <div className="mx-auto mt-2 h-1 w-9 shrink-0 rounded-full bg-muted" aria-hidden />

          <div className="flex flex-col gap-1 px-4 pt-3 pb-2">
            <Drawer.Title className="text-base font-semibold">{title}</Drawer.Title>
            <Drawer.Description
              className={
                description
                  ? 'text-xs text-muted-foreground'
                  : 'sr-only'
              }
            >
              {description ?? title}
            </Drawer.Description>
          </div>

          <div className="flex-1 overflow-y-auto px-4 pb-3">{children}</div>

          {footer && (
            <div className="border-t bg-card/95 px-4 pt-3 pb-4 backdrop-blur">{footer}</div>
          )}
        </Drawer.Content>
      </Drawer.Portal>
    </Drawer.Root>
  );
}
